import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Paper,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff, Save } from "@material-ui/icons";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  helperText: {
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#424242",
    borderRadius: 4,
    marginBottom: theme.spacing(3),
  },
  infoText: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
  },
}));

export default function WebhookConfig({ settings }) {
  const classes = useStyles();

  const [webhookEnabled, setWebhookEnabled] = useState("disabled");
  const [webhookClassificationEnabled, setWebhookClassificationEnabled] = useState("disabled");
  const [webhookCampaignEnabled, setWebhookCampaignEnabled] = useState("disabled");
  const [webhookCampaignMetaResponseEnabled, setWebhookCampaignMetaResponseEnabled] = useState("disabled");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookToken, setWebhookToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);

  // Configurações CRM
  const [crmApiUrl, setCrmApiUrl] = useState("");
  const [crmApiToken, setCrmApiToken] = useState("");
  const [showCrmToken, setShowCrmToken] = useState(false);

  useEffect(() => {
    if (settings && settings.length > 0) {
      const webhookEnabledSetting = settings.find(
        (s) => s.key === "webhookEnabled"
      );
      const webhookClassificationEnabledSetting = settings.find(
        (s) => s.key === "webhookClassificationEnabled"
      );
      const webhookCampaignEnabledSetting = settings.find(
        (s) => s.key === "webhookCampaignEnabled"
      );
      const webhookCampaignMetaResponseEnabledSetting = settings.find(
        (s) => s.key === "webhookCampaignMetaResponseEnabled"
      );
      const webhookUrlSetting = settings.find((s) => s.key === "webhookUrl");
      const webhookTokenSetting = settings.find(
        (s) => s.key === "webhookToken"
      );

      const crmApiUrlSetting = settings.find((s) => s.key === "crmApiUrl");
      const crmApiTokenSetting = settings.find((s) => s.key === "crmApiToken");

      if (webhookEnabledSetting) {
        setWebhookEnabled(webhookEnabledSetting.value || "disabled");
      }
      if (webhookClassificationEnabledSetting) {
        setWebhookClassificationEnabled(webhookClassificationEnabledSetting.value || "disabled");
      }
      if (webhookCampaignEnabledSetting) {
        setWebhookCampaignEnabled(webhookCampaignEnabledSetting.value || "disabled");
      }
      if (webhookCampaignMetaResponseEnabledSetting) {
        setWebhookCampaignMetaResponseEnabled(webhookCampaignMetaResponseEnabledSetting.value || "disabled");
      }
      if (webhookUrlSetting) {
        setWebhookUrl(webhookUrlSetting.value || "");
      }
      if (webhookTokenSetting) {
        setWebhookToken(webhookTokenSetting.value || "");
      }
      if (crmApiUrlSetting) {
        setCrmApiUrl(crmApiUrlSetting.value || "");
      }
      if (crmApiTokenSetting) {
        setCrmApiToken(crmApiTokenSetting.value || "");
      }
    }
  }, [settings]);

  const handleSaveWebhookEnabled = async (value) => {
    setLoading(true);
    try {
      await api.put("/settings/webhookEnabled", { value });
      setWebhookEnabled(value);
      toast.success("Configuração atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar configuração.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveWebhookClassificationEnabled = async (value) => {
    setLoading(true);
    try {
      await api.put("/settings/webhookClassificationEnabled", { value });
      setWebhookClassificationEnabled(value);
      toast.success("Webhook de classificação atualizado com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar webhook de classificação.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveWebhookCampaignEnabled = async (value) => {
    setLoading(true);
    try {
      await api.put("/settings/webhookCampaignEnabled", { value });
      setWebhookCampaignEnabled(value);
      toast.success("Webhook de campanha atualizado com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar webhook de campanha.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveWebhookCampaignMetaResponseEnabled = async (value) => {
    setLoading(true);
    try {
      await api.put("/settings/webhookCampaignMetaResponseEnabled", { value });
      setWebhookCampaignMetaResponseEnabled(value);
      toast.success("Webhook de resposta de campanha META atualizado com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar webhook de resposta de campanha META.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveWebhookUrl = async () => {
    if (webhookEnabled === "enabled" && !webhookUrl) {
      toast.error("URL do Webhook é obrigatória quando habilitado.");
      return;
    }

    setLoading(true);
    try {
      await api.put("/settings/webhookUrl", { value: webhookUrl });
      toast.success("URL do Webhook atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar URL do Webhook.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveWebhookToken = async () => {
    setLoading(true);
    try {
      await api.put("/settings/webhookToken", { value: webhookToken });
      toast.success("Token do Webhook atualizado com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar Token do Webhook.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveCrmApiUrl = async () => {
    setLoading(true);
    try {
      await api.put("/settings/crmApiUrl", { value: crmApiUrl });
      toast.success("URL da API CRM atualizada com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar URL da API CRM.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveCrmApiToken = async () => {
    setLoading(true);
    try {
      await api.put("/settings/crmApiToken", { value: crmApiToken });
      toast.success("Token da API CRM atualizado com sucesso!");
    } catch (err) {
      toast.error("Erro ao atualizar Token da API CRM.");
      console.error(err);
    }
    setLoading(false);
  };

  const handleSaveAll = async () => {
    if (webhookEnabled === "enabled" && !webhookUrl) {
      toast.error("URL do Webhook é obrigatória quando habilitado.");
      return;
    }

    setLoading(true);
    try {
      await Promise.all([
        api.put("/settings/webhookEnabled", { value: webhookEnabled }),
        api.put("/settings/webhookClassificationEnabled", { value: webhookClassificationEnabled }),
        api.put("/settings/webhookCampaignEnabled", { value: webhookCampaignEnabled }),
        api.put("/settings/webhookCampaignMetaResponseEnabled", { value: webhookCampaignMetaResponseEnabled }),
        api.put("/settings/webhookUrl", { value: webhookUrl }),
        api.put("/settings/webhookToken", { value: webhookToken }),
        api.put("/settings/crmApiUrl", { value: crmApiUrl }),
        api.put("/settings/crmApiToken", { value: crmApiToken }),
      ]);
      toast.success("Todas as configurações foram salvas com sucesso!");
    } catch (err) {
      toast.error("Erro ao salvar configurações.");
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className={classes.container}>
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.title}>
          Configurações de Webhook CRM
        </Typography>

        <Box className={classes.infoBox}>
          <Typography className={classes.infoText}>
            Configure o webhook para enviar automaticamente os dados de tickets
            classificados para seu CRM externo. O webhook será disparado após a
            classificação de cada ticket fechado.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Habilitar/Desabilitar Webhook Geral */}
          <Grid item xs={12}>
            <FormControl className={classes.formControl}>
              <InputLabel id="webhook-enabled-label">
                Webhook Geral
              </InputLabel>
              <Select
                labelId="webhook-enabled-label"
                value={webhookEnabled}
                onChange={(e) => handleSaveWebhookEnabled(e.target.value)}
                disabled={loading}
              >
                <MenuItem value="disabled">Desabilitado</MenuItem>
                <MenuItem value="enabled">Habilitado</MenuItem>
              </Select>
              <FormHelperText className={classes.helperText}>
                Habilita ou desabilita o envio de webhooks de forma geral. Os webhooks individuais só funcionarão se esta opção estiver habilitada.
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Separador */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}>
              Webhooks por Tipo de Evento
            </Typography>
          </Grid>

          {/* Webhook de Classificação */}
          <Grid item xs={12} md={6}>
            <FormControl className={classes.formControl}>
              <InputLabel id="webhook-classification-enabled-label">
                Webhook de Classificação
              </InputLabel>
              <Select
                labelId="webhook-classification-enabled-label"
                value={webhookClassificationEnabled}
                onChange={(e) => handleSaveWebhookClassificationEnabled(e.target.value)}
                disabled={loading || webhookEnabled === "disabled"}
              >
                <MenuItem value="disabled">Desabilitado</MenuItem>
                <MenuItem value="enabled">Habilitado</MenuItem>
              </Select>
              <FormHelperText className={classes.helperText}>
                Envia dados do ticket após classificação
                {webhookEnabled === "disabled" && " (Webhook geral desabilitado)"}
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Webhook de Campanha */}
          <Grid item xs={12} md={6}>
            <FormControl className={classes.formControl}>
              <InputLabel id="webhook-campaign-enabled-label">
                Webhook de Campanha
              </InputLabel>
              <Select
                labelId="webhook-campaign-enabled-label"
                value={webhookCampaignEnabled}
                onChange={(e) => handleSaveWebhookCampaignEnabled(e.target.value)}
                disabled={loading || webhookEnabled === "disabled"}
              >
                <MenuItem value="disabled">Desabilitado</MenuItem>
                <MenuItem value="enabled">Habilitado</MenuItem>
              </Select>
              <FormHelperText className={classes.helperText}>
                Envia dados do disparo de cada mensagem de campanha
                {webhookEnabled === "disabled" && " (Webhook geral desabilitado)"}
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Webhook de Resposta de Campanha META */}
          <Grid item xs={12} md={6}>
            <FormControl className={classes.formControl}>
              <InputLabel id="webhook-campaign-meta-response-enabled-label">
                Webhook de Resposta Campanha META
              </InputLabel>
              <Select
                labelId="webhook-campaign-meta-response-enabled-label"
                value={webhookCampaignMetaResponseEnabled}
                onChange={(e) => handleSaveWebhookCampaignMetaResponseEnabled(e.target.value)}
                disabled={loading || webhookEnabled === "disabled"}
              >
                <MenuItem value="disabled">Desabilitado</MenuItem>
                <MenuItem value="enabled">Habilitado</MenuItem>
              </Select>
              <FormHelperText className={classes.helperText}>
                Envia dados quando houver resposta em campanha META
                {webhookEnabled === "disabled" && " (Webhook geral desabilitado)"}
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Separador */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}>
              Configurações de Conexão
            </Typography>
          </Grid>

          {/* URL do Webhook */}
          <Grid item xs={12}>
            <TextField
              label="URL do Webhook"
              variant="outlined"
              fullWidth
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              disabled={loading}
              placeholder="https://seu-crm.com/api/webhook"
              helperText="URL completa onde os dados serão enviados via POST"
              className={classes.formControl}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveWebhookUrl}
              disabled={loading}
              startIcon={<Save />}
              size="small"
            >
              Salvar URL
            </Button>
          </Grid>

          {/* Token do Webhook */}
          <Grid item xs={12}>
            <TextField
              label="Token de Autenticação"
              variant="outlined"
              fullWidth
              type={showToken ? "text" : "password"}
              value={webhookToken}
              onChange={(e) => setWebhookToken(e.target.value)}
              disabled={loading}
              placeholder="seu-token-secreto"
              helperText="Token que será enviado no header 'Authorization: Bearer {token}'"
              className={classes.formControl}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle token visibility"
                      onClick={() => setShowToken(!showToken)}
                      edge="end"
                    >
                      {showToken ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveWebhookToken}
              disabled={loading}
              startIcon={<Save />}
              size="small"
            >
              Salvar Token
            </Button>
          </Grid>

          {/* Separador - Configurações CRM */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" style={{ marginTop: 24, marginBottom: 8, fontWeight: 500 }}>
              Configurações da API CRM (para sincronização de contatos)
            </Typography>
          </Grid>

          {/* URL da API CRM */}
          <Grid item xs={12}>
            <TextField
              label="URL da API CRM"
              variant="outlined"
              fullWidth
              value={crmApiUrl}
              onChange={(e) => setCrmApiUrl(e.target.value)}
              disabled={loading}
              placeholder="http://api.svvcob.com.br:9287/api/external/buscar-devedor"
              helperText="URL da API CRM para buscar devedores e sincronizar informações"
              className={classes.formControl}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveCrmApiUrl}
              disabled={loading}
              startIcon={<Save />}
              size="small"
            >
              Salvar URL CRM
            </Button>
          </Grid>

          {/* Token da API CRM */}
          <Grid item xs={12}>
            <TextField
              label="Token da API CRM"
              variant="outlined"
              fullWidth
              type={showCrmToken ? "text" : "password"}
              value={crmApiToken}
              onChange={(e) => setCrmApiToken(e.target.value)}
              disabled={loading}
              placeholder="gtert3456y7u89i0op"
              helperText="Token de autenticação Bearer para a API CRM"
              className={classes.formControl}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle crm token visibility"
                      onClick={() => setShowCrmToken(!showCrmToken)}
                      edge="end"
                    >
                      {showCrmToken ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveCrmApiToken}
              disabled={loading}
              startIcon={<Save />}
              size="small"
            >
              Salvar Token CRM
            </Button>
          </Grid>

          {/* Botão Salvar Tudo */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSaveAll}
              disabled={loading}
              startIcon={<Save />}
              fullWidth
              className={classes.saveButton}
            >
              Salvar Todas as Configurações
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Informações sobre a estrutura de dados */}
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.title}>
          Estrutura de Dados Enviada
        </Typography>
        <Typography className={classes.infoText} paragraph>
          O webhook enviará um POST request com JSON contendo dados conforme o tipo de evento:
        </Typography>

        <Typography variant="subtitle1" style={{ fontWeight: 500, marginTop: 16, marginBottom: 8 }}>
          Webhook de Classificação (evento: ticket.classified)
        </Typography>
        <Typography
          className={classes.infoText}
          component="div"
          style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
        >
          • Dados do ticket (ID, UUID, status, canal, datas)
          <br />
          • Informações do contato (nome, telefone, email, tags, campos
          customizados)
          <br />
          • Classificação (tipo, categoria, observação)
          <br />
          • Dados do atendente e fila
          <br />
          • Resumo de mensagens (primeira, última, total, mídias)
          <br />
          • Métricas de atendimento (tempo, SLA, respostas)
          <br />
          • Histórico do contato e classificações anteriores
          <br />
          • Dados de satisfação e engajamento
        </Typography>

        <Typography variant="subtitle1" style={{ fontWeight: 500, marginTop: 16, marginBottom: 8 }}>
          Webhook de Campanha (evento: campaign.message.sent)
        </Typography>
        <Typography
          className={classes.infoText}
          component="div"
          style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
        >
          • Dados da campanha (ID, nome, status, estatísticas)
          <br />
          • Informações do disparo (número, mensagem, data de entrega)
          <br />
          • Dados do contato (nome, telefone, email, se é grupo)
          <br />
          • Informações do WhatsApp utilizado
          <br />
          • Configurações da campanha (confirmação, abertura de ticket, mídia)
          <br />
          • Estatísticas gerais (total enviado, entregue, confirmado, taxa de entrega)
          <br />
          • Metadados do webhook (evento, timestamp, source)
        </Typography>
      </Paper>
    </div>
  );
}
